import {ICredentials} from '../../interfaces/credentials';
import {UserService} from '../../services/user/user.service';
import {Component, Input, OnInit} from '@angular/core';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {AppTourService} from '../../services/app-tour/app-tour.service';
import {firstValueFrom, Observable} from 'rxjs';
import {Select, Store} from '@ngxs/store';
import {Loader} from '../../models/loader/loader.redux';
import {ToursEnum} from '../../enums/tour';
import {StorageService} from '../../services/storage/storage.service';
import {LocalStorageEnum} from '../../enums/utils';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {TourAction, TourState} from '../../models/tour';
import Typebot from 'typebot-js';
import {PlanState} from '../../models/plan';
import {SubscriptionDTO} from '../../dtos/billing';
import {ClinicState, IClinic} from '../../models/clinic';

@Component({
  selector: 'app-tour-card',
  templateUrl: './tour-card.component.html',
  styleUrls: ['./tour-card.component.scss']
})
export class TourCardComponent implements OnInit {
  @Select(PlanState.getSubscriptionActive) activeSubscription$: Observable<SubscriptionDTO>;

  @Input() tour: {key: string, value: boolean};
  tourRequireDemoData: (ToursEnum | string)[] = [
    ToursEnum.ADVICE,
    ToursEnum.ASSIGN_SELECT_MEAL_PLAN,
    ToursEnum.ASSIGN_CREATE_MEAL_PLAN,
    ToursEnum.CHECK_SCHEDULE,
    ToursEnum.REGISTER_PATIENT,
    ToursEnum.SCHEDULE_APPOINTMENT,
    ToursEnum.SPHERE,
  ];

  params: Params;

  constructor(
    private appTourService: AppTourService,
    private messageAlertService: MessageAlertService,
    private store: Store,
    private router: Router,
    private storageService: StorageService,
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
  ) { }

  ngOnInit(): void {
    this.params = this.activatedRoute.snapshot.params;
  }

  async startTour(): Promise<void> {
    await firstValueFrom(this.store.dispatch(new Loader(true, 'Cargando el tour')));
    const clinicId = await this.activatedRoute.snapshot.params.clinicId;
    if (!clinicId) return;
    const clinic: IClinic = await firstValueFrom(this.store.selectOnce(ClinicState.getClinicSelected));
    if (!clinic) return;
    // Close typebot
    Typebot.close();
    switch (this.tour.key) {
      case 'registerPatient': { // Registrar paciente
        await this.storageService.setLocalItem(LocalStorageEnum.FORCE_TOUR_NAME, ToursEnum.REGISTER_PATIENT);
        await this.appTourService.initTourRegisterPatientMenu();
        break;
      }
      case 'registerFoodDish': { // registrar receta
        await this.storageService.setLocalItem(LocalStorageEnum.FORCE_TOUR_NAME, ToursEnum.REGISTER_FOOD_DISH);
        await this.appTourService.initTourRegisterFoodDishMenu();
        break;
      }
      case 'registerMealPlan': { // registrar plan de alimentación
        await this.storageService.setLocalItem(LocalStorageEnum.FORCE_TOUR_NAME, ToursEnum.REGISTER_MEAL_PLAN);
        await this.appTourService.initTourRegisterMealPlanMenu();
        break;
      }
      case 'registerBranch': { // registrar sucursal
        if (await this.appTourService.canViewRegisterBranchTour(clinicId)) {
          await this.storageService.setLocalItem(LocalStorageEnum.FORCE_TOUR_NAME, ToursEnum.REGISTER_BRANCH);
          await this.appTourService.initTourRegisterBranchMenu();
        }
        break;
      }
      case 'checkSchedule': { // Agenda
        await this.storageService.setLocalItem(LocalStorageEnum.FORCE_TOUR_NAME, ToursEnum.CHECK_SCHEDULE);
        await firstValueFrom(this.store.dispatch(new TourAction.CreateDemoPatient(clinicId)));
        await firstValueFrom(this.store.dispatch(new TourAction.CreateDemoAppointment(clinicId)));
        await this.appTourService.initTourCheckScheduleMenu();
        break;
      }
      case 'scheduleAppointment': { // Citas
        await this.storageService.setLocalItem(LocalStorageEnum.FORCE_TOUR_NAME, ToursEnum.SCHEDULE_APPOINTMENT);
        await this.router.navigate(['/', 'clinic', clinicId, 'summary']);
        break;
      }
      case 'sphere': { // Esfera
        // this.enableTourLS(ToursEnum.SPHERE);
        // await this.appTourService.showTour('pending', true);
        break;
      }
      case 'schedules': { // Horarios
        await this.storageService.setLocalItem(LocalStorageEnum.FORCE_TOUR_NAME, ToursEnum.SCHEDULES);
        await this.appTourService.initTourRegisterWorkScheduleMenu();
        break;
      }
      case 'expertSchedules': { // Horarios
        await this.storageService.setLocalItem(LocalStorageEnum.FORCE_TOUR_NAME, ToursEnum.EXPERT_SCHEDULES);
        if (clinic.expertCanDefineSchedules) {
          await this.appTourService.initTourWorkSchedulesOnlyExpertSteps();
        } else {
          await this.appTourService.initTourWorkSchedulesOnlyExpertWithoutPermissionToEditSteps();
        }
        break;
      }
      case 'advice': { // Asesoría
        // await this.appTourService.showTour('pending', true);
        await this.storageService.setLocalItem(LocalStorageEnum.FORCE_TOUR_NAME, ToursEnum.ADVICE);
        await this.router.navigate(['/', 'clinic', clinicId, 'summary']);
        break;
      }
      case 'assignCreateMealPlan': {
        // START TOUR CREATE MEAL PLAN
        await firstValueFrom(this.store.dispatch(new TourAction.CreateDemoPatient(clinicId)));
        const demoPatient = await firstValueFrom(this.store.selectOnce(TourState.getDemoPatientCreated));
        if (!demoPatient) return ;

        this.storageService.setLocalItem(LocalStorageEnum.FORCE_TOUR_NAME, ToursEnum.ASSIGN_CREATE_MEAL_PLAN);
        await this.router.navigate(['/', 'clinic', clinicId, 'patients', demoPatient._id, 'meal-plans']);
        break;
      }
      case 'assignSelectMealPlan': {
        // START TOUR SELECT MEAL PLAN
        await firstValueFrom(this.store.dispatch(new TourAction.CreateDemoPatient(clinicId)));
        const demoPatient = await firstValueFrom(this.store.selectOnce(TourState.getDemoPatientCreated));
        this.storageService.setLocalItem(LocalStorageEnum.FORCE_TOUR_NAME, ToursEnum.ASSIGN_SELECT_MEAL_PLAN);
        const expert = this.userService.getExpertId(this.storageService.getLocalItem<ICredentials>(LocalStorageEnum.USER).user);
        await this.router.navigate(['/', 'clinic', clinicId, 'patients', demoPatient._id, 'meal-plans'], {queryParams: {expert}});
        break;
      }
      default: {
        return this.messageAlertService.createMessage('error', 'Tour no disponible.');
      }
    }
    await this.store.dispatch(new TourAction.Force(this.params.clinicId, this.tour.key));
    await firstValueFrom(this.store.dispatch(new Loader(false)));
  }

}
